import * as React from 'react'

import {
  Box,
  Paper,
  Tabs,
  Tab,
} from '@mui/material'

import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import Toolbar from '../../components/Toolbar'
import ProfileSettings from './ProfileSettings';
import PresentationSettings from './PresentationSettings';
import PresentationWizard from './PresentationWizard';
import CompetitorSettings from './CompetitorSettings';

const SETTINGS_BASE = '/settings';

const menuItems = [
  { id: 0, path: 'profile', label: "Profile", component: <ProfileSettings /> },
  { id: 1, path: 'presentation', label: "Presentation", component: <PresentationSettings /> },
  { id: 2, path: 'competitors', label: "Competitors", component: <CompetitorSettings /> },
];

function getTabIndexFromPathname(pathname) {
  if (pathname === SETTINGS_BASE || pathname === `${SETTINGS_BASE}/`) {
    return 0;
  }
  const segment = pathname.slice(SETTINGS_BASE.length + 1).split('/')[0];
  const item = menuItems.find((m) => m.path === segment);
  return item ? item.id : 0;
}

function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const isNewPresentation = pathname === '/settings/presentation/new';
  const editMatch = pathname.match(/^\/settings\/presentation\/(\d+)\/edit$/);
  const isEditPresentation = !!editMatch;
  const selectedTab = getTabIndexFromPathname(pathname);

  const handleTabChange = (e, tabValue) => {
    const item = menuItems.find((m) => m.id === tabValue);
    if (item) {
      navigate(`${SETTINGS_BASE}/${item.path}`);
    }
  };

  const renderContent = () => {
    if (isNewPresentation) {
      return <PresentationWizard />;
    }
    if (isEditPresentation) {
      return <PresentationWizard editId={editMatch[1]} />;
    }
    const selectedItem = menuItems.find((item) => item.id === selectedTab);
    return selectedItem ? selectedItem.component : null;
  };

  // Redirect /settings to /settings/profile
  if (pathname === SETTINGS_BASE || pathname === `${SETTINGS_BASE}/`) {
    return <Navigate to={`${SETTINGS_BASE}/profile`} replace />;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Toolbar />
      <Box
        sx={{ 
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Simple flat tabs */}
        <Box
          sx={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: 'white',
            px: 2,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              '& .MuiTabs-indicator': {
                backgroundColor: '#3B82F6',
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: 48,
                color: '#6B7280',
                fontSize: '0.875rem',
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: '#3B82F6',
                  fontWeight: 500,
                },
              },
            }}
          >
            {menuItems.map((item) => (
              <Tab key={item.id} label={item.label} />
            ))}
          </Tabs>
        </Box>

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: isNewPresentation || isEditPresentation ? '12px' : '24px',
            backgroundColor: '#fafafa',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              padding: isNewPresentation || isEditPresentation ? '16px' : '32px',
              borderRadius: 2,
              backgroundColor: 'white',
              minWidth: 0,
            }}
          >
            {renderContent()}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default Settings;
